import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CustomPaginationTable from '../../components/table/CustomPaginationTable';
import { fetchOrderQuery, setQueryParams, exportOrderQuery } from '../../redux/orderQuerySlice';
import './ViewSales.css';
import { COMPLETED } from '../../utils/constant';

function ViewSales() {
  const dispatch = useDispatch();
  const { data, loading, error, pagination, exportLoading } = useSelector(state => state.orderQuery);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const queryParams = {
      page: 0,
      size: pageSize,
      status: COMPLETED
    };
    dispatch(setQueryParams(queryParams));
    dispatch(fetchOrderQuery(queryParams));
  }, [dispatch, pageSize]);

  const formatDate = (date) => {
    if (!date) return '';
    return date.toISOString().replace('T', ' ').split('.')[0];
  };

  const buildQueryParams = useCallback((page = 0) => {
    const params = {
      page: page,
      size: pageSize,
      status: COMPLETED
    };

    if (startDate) {
      params.startdate = formatDate(startDate);
    }

    if (endDate) {
      params.enddate = formatDate(endDate);
    }

    return params;
  }, [startDate, endDate, pageSize]);

  const handleSearch = useCallback(() => {
    const queryParams = buildQueryParams(0);
    dispatch(setQueryParams(queryParams));
    dispatch(fetchOrderQuery(queryParams));
    setCurrentPage(1);
  }, [dispatch, buildQueryParams]);

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(1);

    const queryParams = {
      page: 0,
      size: pageSize,
      status: COMPLETED
    };
    dispatch(setQueryParams(queryParams));
    dispatch(fetchOrderQuery(queryParams));
  };

  const handleExport = () => {
    const exportParams = {
      status: COMPLETED
    };

    if (startDate) {
      exportParams.startdate = formatDate(startDate);
    }

    if (endDate) {
      exportParams.enddate = formatDate(endDate);
    }

    dispatch(exportOrderQuery({ queryParams: exportParams, fileNamePrefix: 'sales-query' }));
  };

  const handlePageChange = (page) => {
    const apiPage = page - 1;
    const queryParams = buildQueryParams(apiPage);
    dispatch(fetchOrderQuery(queryParams));
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPageSize(newPerPage);
    const queryParams = {
      ...buildQueryParams(0),
      size: newPerPage
    };
    dispatch(fetchOrderQuery(queryParams));
    setCurrentPage(1);
  };

  const completedOrders = data ? data.filter(item => item.subOrderStatus === COMPLETED) : [];

  const tableData = completedOrders.map(item => ({
    orderNo: item.orderNo,
    orderStatus: item.orderStatus,
    purchaseDate: item.createdDatetime || 'N/A',
    email: item.orderDetail?.email || 'N/A',
    iccid: item.orderDetail?.esim?.iccid || 'N/A',
    mvneOrderId: item.orderDetail?.mvneOrderId || 'N/A',
    product: item.orderDetail?.product?.name || 'N/A',
    beforeDiscountPrice: item.originalPrice,
    discountRate: item.orderDetail?.discountRate,
    discountAmount: item.orderDetail?.discountAmount,
    afterDiscountPrice: item.afterPromoPrice
  }));

  const columns = [
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Order Number</div>,
      selector: row => row.orderNo,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.orderNo}
        </div>
      ),
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Order Status</div>,
      selector: row => row.orderStatus,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.orderStatus}
        </div>
      ),
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Purchase Date</div>,
      selector: row => row.purchaseDate,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.purchaseDate}
        </div>
      ),
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Email</div>,
      selector: row => row.email,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.email}
        </div>
      ),
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>ICCID</div>,
      selector: row => row.iccid,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.iccid}
        </div>
      ),
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>MVNE Order ID</div>,
      selector: row => row.mvneOrderId,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.mvneOrderId}
        </div>
      ),
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Product</div>,
      selector: row => row.product,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.product}
        </div>
      ),
      sortable: true,
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>After Discount Price</div>,
      selector: row => `${parseFloat(row.afterDiscountPrice ?? 0).toFixed(2)}`,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {parseFloat(row.afterDiscountPrice ?? 0).toFixed(2)}
        </div>
      ),
      sortable: true,
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Before Discount Price</div>,
      selector: row => row.beforeDiscountPrice != null ? `${parseFloat(row.beforeDiscountPrice).toFixed(2)}` : 'N/A',
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.beforeDiscountPrice != null ? parseFloat(row.beforeDiscountPrice).toFixed(2) : 'N/A'}
        </div>
      ),
      sortable: true,
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Discount Rate</div>,
      selector: row => row.discountRate != null ? `${row.discountRate * 100}%` : 'N/A',
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.discountRate != null ? `${row.discountRate * 100}%` : 'N/A'}
        </div>
      ),
      sortable: true,
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Discount Amount</div>,
      selector: row => row.discountAmount != null ? `${parseFloat(row.discountAmount).toFixed(2)}` : 'N/A',
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.discountAmount != null ? parseFloat(row.discountAmount).toFixed(2) : 'N/A'}
        </div>
      ),
      sortable: true,
      wrap: true
    }
  ];

  return (
    <div className='view-sales-container'>
      <div className="view-sales-content">
        <h3>View Sales</h3>

        <div className="view-sales-search-section">
          <div className="view-sales-search-form">
            <div className="view-sales-form-group">
              <DatePicker
                selected={startDate}
                onChange={date => setStartDate(date)}
                dateFormat="yyyy-MM-dd"
                placeholderText="start date"
                className="view-sales-date-input"
                showIcon
              />
            </div>

            <div className="view-sales-form-group">
              <DatePicker
                selected={endDate}
                onChange={date => setEndDate(date)}
                dateFormat="yyyy-MM-dd"
                placeholderText="end date"
                className="view-sales-date-input"
                showIcon
              />
            </div>

            <div className="view-sales-button-group">
              <button
                className="view-sales-search-btn"
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? 'SEARCHING...' : 'SEARCH'}
              </button>
              <button
                className="view-sales-search-btn"
                onClick={handleReset}
                disabled={loading}
              >
                RESET
              </button>
              <button
                className="view-sales-search-btn"
                onClick={handleExport}
                disabled={exportLoading || loading || completedOrders.length === 0}
              >
                {exportLoading ? 'EXPORTING...' : 'EXPORT'}
              </button>
            </div>
          </div>
        </div>

        <div className="view-sales-table-section">
          <CustomPaginationTable
            data={tableData}
            totalRows={pagination.totalRecords || 0}
            loading={loading}
            perPage={pagination.pageSize || 10}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            columns={columns}
            onPerRowsChange={handlePerRowsChange}
            isHiddenPag={false}
          />
        </div>
      </div>
    </div>
  );
}

export default ViewSales;